import React, { useMemo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';

import { appsSelector, userIdSelector, requestAccessToModuleAction } from 'domain/env';

import * as ACL from 'domain/restriction';
import { Apps } from 'domain/env/helpers';

import AppSelectorIcon from '@mui/icons-material/Apps';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SvgIcon from '@mui/material/SvgIcon';
import TalkToUsDialog from './components/TalkToUsDialog';
import { Menu, MenuItem, IconWrapper } from './StyledComponents';

import useToggle from 'hooks/useToggle';
import useRedirectBetweenApps from './hooks/useRedirectBetweenApps';

import { ReactComponent as APIcon } from './icons/APIcon.svg';
import { ReactComponent as APIconSelected } from './icons/APIconSelected.svg';
import { ReactComponent as APIconInactive } from './icons/APIconInactive.svg';
import { ReactComponent as MCIcon } from './icons/MCIcon.svg';
import { ReactComponent as MCIconSelected } from './icons/MCIconSelected.svg';
import { ReactComponent as MCIconInactive } from './icons/MCIconInactive.svg';
import { ReactComponent as PAYIcon } from './icons/PAYIcon.svg';
import { ReactComponent as PAYIconSelected } from './icons/PAYIconSelected.svg';
import { ReactComponent as PAYIconInactive } from './icons/PAYIconInactive.svg';

import { alpha, useTheme } from '@mui/material';

import elements from 'components/elements';

const mapStateToProps = (state) => ({
  apps: appsSelector(state),
  userEmail: userIdSelector(state),
  isGranted: ACL.isGranted(state),
});

const ApplicationDropdown = () => {
  const dispatch = useDispatch();
  const { palette } = useTheme();
  const { formatMessage } = useIntl();
  const [toggleDialog, onToggleDialog] = useToggle(false);
  const popupState = usePopupState({ variant: 'popover', popupId: 'AppsDropdown' });
  const { apps, userEmail, isGranted } = useSelector(mapStateToProps);
  const [ticketPayload, setTicketPayload] = useState();
  const [ticketModule, setTicketModule] = useState('');
  const { redirectBetweenApps, isPayModule } = useRedirectBetweenApps();

  const getApp = useCallback(
    (appCode) => {
      const app = apps.get(appCode);

      if (app && appCode === Apps.PAY) {
        return app.set('active', app.active && isGranted(ACL.IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY));
      }

      return app;
    },
    [apps, isGranted],
  );

  const requestAccesstoModule = useCallback(
    (payload) => () =>
      new Promise((resolve, reject) => {
        dispatch(requestAccessToModuleAction({ payload, resolve, reject }));
      }).finally(() => {
        onToggleDialog();
        popupState.close();
      }),
    [dispatch, onToggleDialog, popupState],
  );

  const onOpenDialog = useCallback(() => {
    onToggleDialog();
  }, [onToggleDialog]);

  const selectedState = useMemo(
    () => ({
      [Apps.AP]: isPayModule ? 'unselected' : 'selected',
      [Apps.PAY]: isPayModule ? 'selected' : 'unselected',
      [Apps.MC]: 'unselected',
    }),
    [isPayModule],
  );

  const menuItems = useMemo(() => {
    const activeItems = {
      [Apps.AP]: {
        selected: {
          icon: <SvgIcon component={APIconSelected} inheritViewBox fontSize="inherit" />,
        },
        unselected: {
          icon: <SvgIcon component={APIcon} inheritViewBox fontSize="inherit" />,
          onClick: () => {
            popupState.close();
            redirectBetweenApps();
          },
        },
      },
      [Apps.MC]: {
        selected: {
          icon: <SvgIcon component={MCIconSelected} inheritViewBox fontSize="inherit" />,
        },
        unselected: {
          icon: <SvgIcon component={MCIcon} inheritViewBox fontSize="inherit" />,
          onClick: () => {
            popupState.close();
            const appUrl = apps.getIn(['MC', 'appUrl']);
            if (appUrl) {
              // this simply opens url in new tab/window according to browser preferences
              Object.assign(document.createElement('a'), {
                target: '_blank',
                rel: 'noopener noreferrer',
                href: appUrl,
              }).click();
            }
          },
        },
      },
      [Apps.PAY]: {
        selected: {
          icon: <SvgIcon component={PAYIconSelected} inheritViewBox fontSize="inherit" />,
        },
        unselected: {
          icon: <SvgIcon component={PAYIcon} inheritViewBox fontSize="inherit" />,
          onClick: () => {
            popupState.close();
            redirectBetweenApps();
          },
        },
      },
    };
    const inActiveItems = {
      [Apps.AP]: {
        selected: {
          icon: <SvgIcon component={APIconInactive} inheritViewBox fontSize="inherit" />,
        },
        unselected: {
          icon: <SvgIcon component={APIconInactive} inheritViewBox fontSize="inherit" />,
        },
      },
      [Apps.MC]: {
        selected: {
          icon: <SvgIcon component={MCIconInactive} inheritViewBox fontSize="inherit" />,
        },
        unselected: {
          icon: <SvgIcon component={MCIconInactive} inheritViewBox fontSize="inherit" />,
          onClick: () => {
            const payload = {
              subject: 'Request for upgrade to Monthly Closing',
              description: `User ${userEmail} requested for upgrade to Monthly Closing. Contact with customer is required`,
              custom_fields: {
                cf_app_url: window.location.href,
                cf_product: 'Dokka',
              },
            };
            setTicketPayload(payload);
            setTicketModule(Apps.MC);
            onOpenDialog();
          },
        },
      },
      [Apps.PAY]: {
        selected: {
          icon: <SvgIcon component={PAYIconInactive} inheritViewBox fontSize="inherit" />,
        },
        unselected: {
          icon: <SvgIcon component={PAYIconInactive} inheritViewBox fontSize="inherit" />,
          onClick: () => {
            const payload = {
              subject: 'Request for upgrade to Monthly PAY',
              description: `User ${userEmail} requested for upgrade to Monthly PAY. Contact with customer is required`,
              custom_fields: {
                cf_app_url: window.location.href,
                cf_product: 'Dokka',
              },
            };
            setTicketPayload(payload);
            setTicketModule(Apps.PAY);
            onOpenDialog();
          },
        },
      },
    };
    return [
      {
        value: Apps.AP,
      },
      {
        value: Apps.PAY,
      },
      {
        value: Apps.MC,
      },
    ].map((i) => {
      const appCode = i.value;
      const app = getApp(appCode);

      const items = app?.active ? activeItems : inActiveItems;
      const label = formatMessage({
        id: `app.selector.${appCode}.label`,
        defaultMessage: app?.appName,
      });

      return { ...i, ...items[i.value][selectedState[appCode]], label };
    });
  }, [apps, onOpenDialog, popupState, userEmail, formatMessage, redirectBetweenApps, selectedState, getApp]);

  return (
    <>
      <IconButton
        size="small"
        sx={{
          boxShadow: `0 0 0 1px ${popupState.isOpen ? palette.primary.main : palette.grey[400]} inset`,
          backgroundColor: `${popupState.isOpen ? alpha(palette.primary.main, 0.3) : 'transparent'}`,
          '&:hover': {
            backgroundColor: alpha(palette.primary.main, 0.3),
          },
        }}
        {...bindTrigger(popupState)}
        data-element={elements.header.appSelector.container}
      >
        <AppSelectorIcon color="primary" />
      </IconButton>
      <Menu
        {...bindMenu(popupState)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {menuItems.map((item) => (
          <MenuItem key={item.value} onClick={item.onClick} data-element={elements.header.appSelector.menuItem}>
            <Stack>
              <IconWrapper>{item.icon}</IconWrapper>
              <Typography variant="body1">{item.label}</Typography>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
      <TalkToUsDialog
        open={toggleDialog}
        onClose={onToggleDialog}
        module={ticketModule}
        action={requestAccesstoModule(ticketPayload)}
      />
    </>
  );
};

export default ApplicationDropdown;
